import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";

import PaginatedCatalogAutocomplete from "../../../baseDatos/itemsDeCompra/components/PaginatedCatalogAutocomplete";

const controlSx = {
  width: { xs: "100%", sm: 180 },
  height: 38,
  borderRadius: "33px",
  bgcolor: "neutral.100",
  color: "primary.900",
  fontSize: 13,
  fontWeight: 700,
  boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "secondary.500",
  },
  "& .MuiSelect-select": { display: "flex", alignItems: "center", py: 0 },
};

function ClearAdornment({ label, onClear }) {
  return (
    <InputAdornment
      position="end"
      sx={{ position: "absolute", right: 8, color: "primary.900" }}
    >
      <IconButton
        size="small"
        aria-label={label}
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.stopPropagation();
          onClear();
        }}
        sx={{ width: 24, height: 24, opacity: 0.7 }}
      >
        <ClearIcon sx={{ fontSize: 17 }} />
      </IconButton>
    </InputAdornment>
  );
}

function FilterSelect({ value, placeholder, options, disabled, onChange }) {
  const selectedLabel = options.find(
    (option) => String(option.value) === String(value)
  )?.label;
  const safeValue = selectedLabel ? value : "";
  const allLabel =
    placeholder === "Tipo" || placeholder === "Sector"
      ? "Todos"
      : `Todas las ${placeholder.toLowerCase()}s`;

  return (
    <Select
      value={safeValue}
      onChange={(event) => onChange(event.target.value)}
      displayEmpty
      size="small"
      disabled={disabled}
      IconComponent={safeValue ? () => null : undefined}
      inputProps={{ "aria-label": `Filtro ${placeholder.toLowerCase()}` }}
      sx={{
        ...controlSx,
        "& .MuiSelect-select": {
          ...controlSx["& .MuiSelect-select"],
          pr: safeValue ? "32px !important" : undefined,
        },
      }}
      renderValue={() => selectedLabel || placeholder}
      endAdornment={
        safeValue ? (
          <ClearAdornment
            label={`Limpiar filtro ${placeholder.toLowerCase()}`}
            onClear={() => onChange("")}
          />
        ) : null
      }
    >
      <MenuItem value="">{allLabel}</MenuItem>
      {options.map((option) => (
        <MenuItem key={option.value} value={String(option.value)}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
}

function normalizeIngredientLabel(ingredient) {
  return ingredient?.name || "";
}

export default function StockInventoryFilters({
  categoryOptions,
  categoriesLoading,
  categoryValue,
  sectorValue,
  typeValue,
  ingredientValue,
  searchInput,
  loadIngredients,
  normalizeIngredient,
  onCategoryChange,
  onSectorChange,
  onTypeChange,
  onIngredientChange,
  onSearchInputChange,
  onSearchCommit,
  onSearchClear,
}) {
  const typeOptions = [
    { value: "INGREDIENT", label: "Insumo" },
    { value: "PREPARED_PRODUCT", label: "Elaborado" },
  ];
  const sectorOptions = [
    { value: "Cocina", label: "Cocina" },
    { value: "Barra", label: "Barra" },
  ];
  const categorySelectOptions = categoryOptions.map((category) => ({
    value: category.id,
    label: category.name,
  }));
  const ingredientDisabled = typeValue !== "INGREDIENT";

  return (
    <Stack
      direction={{ xs: "column", lg: "row" }}
      spacing={1.25}
      useFlexGap
      sx={{
        width: "100%",
        flexWrap: { xs: "wrap", lg: "nowrap" },
        alignItems: { xs: "stretch", lg: "center" },
      }}
    >
      <FilterSelect
        value={categoryValue}
        placeholder="Categoría"
        disabled={categoriesLoading}
        options={categorySelectOptions}
        onChange={onCategoryChange}
      />

      <FilterSelect
        value={sectorValue}
        placeholder="Sector"
        options={sectorOptions}
        onChange={onSectorChange}
      />

      <FilterSelect
        value={typeValue}
        placeholder="Tipo"
        options={typeOptions}
        onChange={onTypeChange}
      />

      <Stack sx={{ width: { xs: "100%", sm: 180 } }}>
        <PaginatedCatalogAutocomplete
          active
          label="Insumo"
          placeholder="Insumo"
          value={ingredientValue}
          disabled={ingredientDisabled}
          loadPage={loadIngredients}
          normalizeOption={normalizeIngredient}
          getOptionLabel={normalizeIngredientLabel}
          getOptionDescription={(ingredient) =>
            ingredient?.categoryName ? `Categoría: ${ingredient.categoryName}` : " "
          }
          onChange={onIngredientChange}
          compact
          required={false}
        />
      </Stack>

      <TextField
        value={searchInput}
        onChange={(event) => onSearchInputChange(event.target.value)}
        onBlur={(event) => onSearchCommit(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          onSearchCommit(event.currentTarget.value);
        }}
        placeholder="Buscar ítem"
        size="small"
        slotProps={{ htmlInput: { maxLength: 150 } }}
        sx={{
          width: { xs: "100%", sm: 260 },
          ml: { lg: "auto" },
          "& .MuiOutlinedInput-root": {
            height: 38,
            borderRadius: "33px",
            bgcolor: "neutral.50",
            color: "primary.900",
            fontSize: 13,
            fontWeight: 600,
            boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
            "& fieldset": { border: "none" },
            "&:hover fieldset": { border: "none" },
          },
          "& .MuiInputBase-input::placeholder": {
            color: "neutral.700",
            opacity: 0.9,
            fontWeight: 600,
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "primary.900", fontSize: 18 }} />
            </InputAdornment>
          ),
          endAdornment: searchInput ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="Limpiar búsqueda"
                onClick={onSearchClear}
                sx={{ width: 24, height: 24, opacity: 0.7 }}
              >
                <ClearIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
    </Stack>
  );
}
